// Shared form-fields surface for Deliverable Create/Edit. Consumed by
// DeliverableFormModal (write), DeliverableInlineExpand (write), and
// DeliverableDetailModal (read-only). Inputs are controlled; parent owns
// state + validation. recurrenceRule is an optional sub-block rendered
// via shared RecurrenceRuleInput. milestoneId is a select populated by
// the parent (Tab supplies the list of milestones on the contract).
// In edit mode (isEditMode=true, non-recurring only), renders an editable
// lifecycle log fieldset below the status select. Events added there are
// intent-only (type + date); provenance is stamped by the server.

import { type ChangeEvent, type ReactNode, useState } from "react";
import { ChevronRight, X } from "lucide-react";
import EvidenceLinksField from "../shared/EvidenceLinksField";
import RecurrenceRuleInput from "../shared/RecurrenceRuleInput";
import { compareSowNumbers } from "../shared/sowPickerSearch";
import ClauseReferenceTypeahead from "./ClauseReferenceTypeahead";
import RecurrencePreviewLine from "./RecurrencePreviewLine";
import { canTransition, validateLifecycleChain } from "./lifecycleTransitions";
import type {
  DeliverableLifecycleEvent,
  DeliverableLifecycleEventReplaceInput,
  DeliverableOwner,
  DeliverableType,
  LifecycleEventType,
  Milestone,
  RagRating,
  Sow,
} from "../../../../types";
import type {
  DeliverableFieldErrors,
  DeliverableFormState,
} from "./deliverableFormState";

export type DeliverableFormMode = "parent" | "occurrence" | "oneoff";

export interface DeliverableFormFieldsProps {
  value: DeliverableFormState;
  onChange: (patch: Partial<DeliverableFormState>) => void;
  errors?: DeliverableFieldErrors;
  disabled?: boolean;
  /** Milestones on the same contract (supplied by the Tab). Excludes archived. */
  milestones?: Milestone[];
  /** SOWs on the same contract (supplied by the Tab). Active SOWs (non-
   *  archived, non-cancelled) drive the dropdown options. A currently-
   *  attached SOW that is archived/cancelled is still shown so the user
   *  can see what they're attached to — labelled with a status suffix. */
  sows?: Sow[];
  idPrefix: string;
  /**
   * Controls which subset of fields surface. `"parent"` hides per-
   * occurrence fields (status/RAG/due/evidence) but the recurrence
   * rule stays editable. Default heuristic: if the
   * form has a recurrence rule treat it as parent, otherwise
   * occurrence/one-off get full fields.
   */
  mode?: DeliverableFormMode;
  /**
   * Locks the RAG radio group with an explanatory tooltip. Set when
   * the deliverable derives to overdue — RAG is forced to Red and
   * the user must change status to adjust.
   */
  ragLocked?: boolean;
  /**
   * Existing clause-references on this contract. When provided the
   * Clause reference field upgrades from a plain text input to a
   * combo-box (ClauseReferenceTypeahead) so users can pick a
   * convention rather than re-type one. Free-text fallback still
   * accepts brand-new references.
   */
  clauseSuggestions?: string[];
  /** Effective end date of the parent contract (admin-maintained). Passed
   *  through for Task 7's end-date helper text and serialiser auto-fill. */
  contractEndDate?: string;
  /**
   * When true (edit mode), renders the editable lifecycle log fieldset
   * below the status select. Hidden in create mode because new deliverables
   * always start with an empty lifecycle. Also hidden for recurring parents
   * (recurrenceRule present or mode="parent") — occurrences carry their own
   * lifecycle independently.
   */
  isEditMode?: boolean;
  /**
   * Seed for the lifecycle log editor. Pass the deliverable's existing
   * lifecycleEvents array (from the server) when opening in edit mode.
   * The editor initialises from this array and syncs changes back via
   * onChange({ lifecycleEventsReplace: events }).
   */
  sourceLifecycleEvents?: DeliverableLifecycleEvent[];
}

const DELIVERABLE_TYPES: { value: DeliverableType; label: string }[] = [
  { value: "deliverable", label: "Deliverable" },
  { value: "obligation", label: "Obligation" },
];

const OWNERS: { value: DeliverableOwner; label: string }[] = [
  { value: "supplier", label: "Supplier" },
  { value: "customer", label: "Customer" },
  { value: "joint", label: "Joint" },
];

const RAG_OPTIONS: { value: RagRating; label: string }[] = [
  { value: "red", label: "Red" },
  { value: "amber", label: "Amber" },
  { value: "green", label: "Green" },
];

// Persist the Advanced disclosure across remounts so power users
// don't have to re-open it for every row they touch in a session.
// The key is shared across all DeliverableFormFields mounts in the
// session — opening it on one row remembers for the next.
const ADVANCED_STORAGE_KEY = "cgn31:deliverable-form:advanced-open";

function readAdvancedOpen(): boolean {
  try {
    return window.sessionStorage.getItem(ADVANCED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeAdvancedOpen(open: boolean): void {
  try {
    window.sessionStorage.setItem(ADVANCED_STORAGE_KEY, String(open));
  } catch {
    /* sessionStorage unavailable — silently degrade */
  }
}

export default function DeliverableFormFields({
  value,
  onChange,
  errors = {},
  disabled,
  milestones = [],
  sows,
  idPrefix,
  mode,
  ragLocked,
  clauseSuggestions,
  contractEndDate,
  isEditMode,
  sourceLifecycleEvents,
}: DeliverableFormFieldsProps) {
  const patch =
    <K extends keyof DeliverableFormState>(field: K) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      onChange({ [field]: e.target.value } as Pick<DeliverableFormState, K>);

  // Parent mode hides per-occurrence fields (status / RAG / evidence /
  // due date / milestone) because those are occurrence concerns. The
  // recurrence rule stays editable in every mode — changing it on a
  // parent flows through shouldAutoOpenOnUpdate so occurrence
  // regeneration triggers automatically.
  const isRecurringParent =
    mode === "parent" || (!mode && !!value.recurrenceRule);

  // Active SOWs only — archived/cancelled excluded from the picker so
  // the user can't *newly* attach to a non-current SOW. The currently-
  // attached SOW is force-included even when archived/cancelled, with
  // a status suffix on the label, so the form reflects stored state.
  const sowOptions: { id: string; label: string }[] = (() => {
    if (!sows || sows.length === 0) return [];
    const active = sows.filter(
      (s) => !s.archivedAt && s.status !== "cancelled",
    );
    const sorted = [...active].sort((a, b) =>
      compareSowNumbers(a.number, b.number),
    );
    const opts = sorted.map((s) => ({
      id: s.id,
      label: `${s.number} · ${s.name}`,
    }));
    if (value.sowId && !opts.some((o) => o.id === value.sowId)) {
      const attached = sows.find((s) => s.id === value.sowId);
      if (attached) {
        const suffix = attached.archivedAt
          ? " (archived)"
          : attached.status === "cancelled"
          ? " (cancelled)"
          : "";
        opts.push({
          id: attached.id,
          label: `${attached.number} · ${attached.name}${suffix}`,
        });
      }
    }
    return opts;
  })();

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="label">
          Name <span className="text-red-600">*</span>
        </label>
        <input
          id={`${idPrefix}-name`}
          className="input"
          value={value.name}
          onChange={patch("name")}
          disabled={disabled}
          maxLength={200}
          aria-invalid={!!errors.name}
        />
        {errors.name && (
          <p className="mt-1 text-caption text-red-600 dark:text-red-400">
            {errors.name}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-type`} className="label">
            Type <span className="text-red-600">*</span>
          </label>
          <select
            id={`${idPrefix}-type`}
            className="input"
            value={value.deliverableType}
            onChange={patch("deliverableType")}
            disabled={disabled}
          >
            {DELIVERABLE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${idPrefix}-owner`} className="label">
            Owner <span className="text-red-600">*</span>
          </label>
          <select
            id={`${idPrefix}-owner`}
            className="input"
            value={value.owner}
            onChange={patch("owner")}
            disabled={disabled}
          >
            {OWNERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          {clauseSuggestions ? (
            <ClauseReferenceTypeahead
              id={`${idPrefix}-clauseref`}
              label="Clause reference"
              value={value.clauseReference}
              onChange={(next) => onChange({ clauseReference: next })}
              suggestions={clauseSuggestions}
            />
          ) : (
            <>
              <label htmlFor={`${idPrefix}-clauseref`} className="label">
                Clause reference
              </label>
              <input
                id={`${idPrefix}-clauseref`}
                className="input"
                value={value.clauseReference}
                onChange={patch("clauseReference")}
                disabled={disabled}
                maxLength={100}
                placeholder="e.g. Clause 7.2"
              />
            </>
          )}
        </div>
        {!isRecurringParent && (
          <div>
            <label htmlFor={`${idPrefix}-milestone`} className="label">
              Milestone
            </label>
            <select
              id={`${idPrefix}-milestone`}
              className="input"
              value={value.milestoneId}
              onChange={patch("milestoneId")}
              disabled={disabled}
            >
              <option value="">— None —</option>
              {milestones.map((m) => (
                <option key={m.id} value={m.id}>
                  #{m.milestoneNumber}
                  {m.milestoneSuffix ?? ""} {m.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-description`} className="label">
          Description
        </label>
        <textarea
          id={`${idPrefix}-description`}
          className="input min-h-[60px]"
          value={value.description}
          onChange={patch("description")}
          disabled={disabled}
          maxLength={500}
        />
      </div>

      {sowOptions.length > 0 && (
        <div>
          <label htmlFor={`${idPrefix}-sow`} className="label">
            SOW
          </label>
          <select
            id={`${idPrefix}-sow`}
            data-testid="deliverable-sow-picker"
            className="input"
            value={value.sowId ?? ""}
            onChange={(e) =>
              onChange({ sowId: e.target.value === "" ? null : e.target.value })
            }
            disabled={disabled}
          >
            <option value="">— None —</option>
            {sowOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {!isRecurringParent && (
        <>
          {/* Due date sits in its own half-width column (the Status select that
              used to share this row is gone — status now derives from the
              lifecycle log). */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label htmlFor={`${idPrefix}-due`} className="label">
                Due date
              </label>
              <input
                id={`${idPrefix}-due`}
                type="date"
                className="input"
                value={value.dueDate}
                onChange={patch("dueDate")}
                disabled={disabled}
                aria-invalid={!!errors.dueDate}
              />
              {errors.dueDate && (
                <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                  {errors.dueDate}
                </p>
              )}
            </div>
          </div>

          <fieldset
            data-testid="deliverable-rag-fieldset"
            title={
              ragLocked
                ? "Overdue items are automatically Red. The rating is set on save and adjusts as the deliverable progresses through its lifecycle."
                : undefined
            }
          >
            <legend className="label">RAG rating</legend>
            <div className="flex flex-wrap gap-3">
              <label className="inline-flex items-center gap-2 text-caption text-slate-700 dark:text-slate-300">
                <input
                  type="radio"
                  name={`${idPrefix}-rag`}
                  value=""
                  checked={value.ragRating === ""}
                  onChange={() => onChange({ ragRating: "" })}
                  disabled={disabled || ragLocked}
                  className="accent-brand-600"
                />
                Not set
              </label>
              {RAG_OPTIONS.map((r) => (
                <label
                  key={r.value}
                  className="inline-flex items-center gap-2 text-caption text-slate-700 dark:text-slate-300"
                >
                  <input
                    type="radio"
                    name={`${idPrefix}-rag`}
                    value={r.value}
                    checked={value.ragRating === r.value}
                    onChange={() => onChange({ ragRating: r.value })}
                    disabled={disabled || ragLocked}
                    className="accent-brand-600"
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </fieldset>
        </>
      )}

      {/*
        Parent mode keeps the per-occurrence fields hidden (status /
        RAG / evidence / due-date stay out) but exposes the recurrence
        rule as editable inputs. Changing frequency / start / end
        flows through the existing GenerateOccurrencesDialog orphan-
        scheduling path (shouldAutoOpenOnUpdate) when the tab receives
        the save.
      */}
      <RecurrenceRuleInput
        value={value.recurrenceRule}
        onChange={(rule) => onChange({ recurrenceRule: rule })}
        disabled={disabled}
        contractEndDate={contractEndDate}
      />
      {value.recurrenceRule && (
        <RecurrencePreviewLine
          frequency={value.recurrenceRule.frequency}
          interval={value.recurrenceRule.interval ?? 1}
          startDate={value.recurrenceRule.startDate ?? ""}
          endDate={value.recurrenceRule.endDate ?? ""}
        />
      )}
      {errors.recurrenceStartDate && (
        <p className="mt-1 text-caption text-red-600 dark:text-red-400">
          {errors.recurrenceStartDate}
        </p>
      )}
      {errors.recurrenceEndDate && (
        <p className="mt-1 text-caption text-red-600 dark:text-red-400">
          {errors.recurrenceEndDate}
        </p>
      )}

      {/* Editable lifecycle log — the uniform edit surface for every
          non-recurring deliverable. The lifecycle event log is the single
          source of truth for status; the legacy status-select is gone.
          Log-less rows (brand-new not-started) seed an empty array so the
          editor still renders and a first correction recomputes derived
          state. Recurring parents generate occurrences which carry their own
          lifecycle independently. */}
      {isEditMode && !isRecurringParent && (
        <LifecycleLogEditor
          initialEvents={sourceLifecycleEvents ?? []}
          disabled={disabled}
          onEventsChange={(events) => onChange({ lifecycleEventsReplace: events })}
        />
      )}

      <AdvancedDisclosure>
        {!isRecurringParent && (
          <EvidenceLinksField
            value={value.evidenceLinks}
            onChange={(v) => onChange({ evidenceLinks: v })}
            disabled={disabled}
          />
        )}
        <div>
          <label htmlFor={`${idPrefix}-notes`} className="label">
            Notes
          </label>
          <textarea
            id={`${idPrefix}-notes`}
            className="input min-h-[80px]"
            value={value.notes}
            onChange={patch("notes")}
            disabled={disabled}
            maxLength={2000}
          />
        </div>
      </AdvancedDisclosure>
    </div>
  );
}

// ─── Lifecycle log editor ─────────────────────────────────────────────────────
// Editable view of a deliverable's lifecycle event log. Each row allows
// the user to correct the date, edit the optional comment, or delete the
// event. New events are added via the "+ Add transition" chip menu.
// Local state manages the working array; changes are emitted via onEventsChange
// on every mutation so the parent can include lifecycleEventsReplace in the
// PUT payload on save.

// Labels for the 8 chips in the Add transition menu. Excludes `closed` and
// `reverted` which are not manually reachable via the editor (they go through
// AcceptanceGateDialog / LifecycleTransitionDialog).
const CHIP_EVENT_TYPES: readonly LifecycleEventType[] = [
  "submitted",
  "under-review",
  "revision-requested",
  "conditionally-accepted",
  "awaiting-resubmission",
  "failed-acceptance",
  "accepted",
  "rejected",
];

const EVENT_LABELS: Record<LifecycleEventType, string> = {
  submitted: "Submitted",
  "under-review": "Under review",
  "revision-requested": "Revision requested",
  "conditionally-accepted": "Conditionally accepted",
  "awaiting-resubmission": "Awaiting resubmission",
  "failed-acceptance": "Failed acceptance",
  accepted: "Accepted",
  rejected: "Rejected",
  closed: "Closed",
  reverted: "Reverted",
};

// Single-use terminal events — disable the chip once one is present.
const SINGLE_USE_TERMINAL: ReadonlySet<LifecycleEventType> = new Set<LifecycleEventType>([
  "accepted",
  "rejected",
]);

interface LifecycleLogEditorProps {
  initialEvents: DeliverableLifecycleEventReplaceInput[];
  disabled?: boolean;
  onEventsChange: (events: DeliverableLifecycleEventReplaceInput[]) => void;
}

function LifecycleLogEditor({
  initialEvents,
  disabled,
  onEventsChange,
}: LifecycleLogEditorProps) {
  const [events, setEvents] = useState<DeliverableLifecycleEventReplaceInput[]>(
    () => [...initialEvents],
  );
  const [chipMenuOpen, setChipMenuOpen] = useState(false);
  // Re-seed working events whenever the parent provides a new initialEvents
  // reference (e.g. server refresh delivering a new array). Guards on the
  // reference so local edits are preserved between keystrokes.
  const [prevInitialEvents, setPrevInitialEvents] = useState(initialEvents);
  if (initialEvents !== prevInitialEvents) {
    setPrevInitialEvents(initialEvents);
    setEvents([...initialEvents]);
  }

  const today = new Date().toISOString().slice(0, 10);

  const terminalState = events.length > 0 ? events[events.length - 1].type : "not-started";
  const presentTypes = new Set(events.map((e) => e.type));

  const validationResult = validateLifecycleChain(events);

  const updateEvents = (next: DeliverableLifecycleEventReplaceInput[]) => {
    setEvents(next);
    onEventsChange(next);
  };

  const handleDeleteEvent = (index: number) => {
    updateEvents(events.filter((_, i) => i !== index));
  };

  const handleDateChange = (index: number, date: string) => {
    const next = events.map((ev, i) =>
      i === index ? { ...ev, date } : ev,
    );
    updateEvents(next);
  };

  const handleCommentChange = (index: number, comment: string) => {
    const next = events.map((ev, i) =>
      i === index ? { ...ev, comment: comment || undefined } : ev,
    );
    updateEvents(next);
  };

  const handleAddChip = (type: LifecycleEventType) => {
    // Intent only. Provenance (who / when recorded) is server-owned: the
    // backend stamps the authenticated actor on any event it cannot match
    // to a stored one, so the editor must not invent a value here.
    const newEvent: DeliverableLifecycleEventReplaceInput = {
      type,
      date: today,
    };
    updateEvents([...events, newEvent]);
    setChipMenuOpen(false);
  };

  return (
    <fieldset
      data-testid="lifecycle-log-editor"
      className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50"
    >
      <legend className="label px-1 text-caption font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Lifecycle history
      </legend>

      {events.length === 0 ? (
        <p className="text-caption text-slate-500 dark:text-slate-400">
          No lifecycle events recorded.
        </p>
      ) : (
        <ol className="space-y-2" aria-label="Lifecycle events">
          {events.map((event, i) => {
            const showAttempt =
              typeof event.attemptIndex === "number" &&
              event.attemptIndex > 0 &&
              (event.type === "failed-acceptance" ||
                event.type === "awaiting-resubmission");
            return (
              <li
                key={`${event.type}-${i}`}
                data-testid={`lifecycle-editor-row-${i}`}
                className="flex flex-wrap items-start gap-2"
              >
                {/* Event label + attempt chip */}
                <div className="flex min-w-[10rem] shrink-0 items-center gap-1.5 pt-1.5">
                  <span className="text-body-sm font-medium text-slate-700 dark:text-slate-300">
                    {EVENT_LABELS[event.type]}
                  </span>
                  {showAttempt && (
                    <span
                      data-testid={`lifecycle-editor-attempt-${i}`}
                      className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-caption font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                    >
                      Attempt {event.attemptIndex}
                    </span>
                  )}
                </div>
                {/* Date input */}
                <div className="flex flex-col gap-0.5">
                  <label
                    htmlFor={`lifecycle-date-${i}`}
                    className="sr-only"
                  >
                    Date
                  </label>
                  <input
                    id={`lifecycle-date-${i}`}
                    type="date"
                    value={event.date}
                    disabled={disabled}
                    onChange={(e) => handleDateChange(i, e.target.value)}
                    className="input text-body-sm"
                    aria-label="Date"
                  />
                </div>
                {/* Comment input */}
                <input
                  type="text"
                  value={event.comment ?? ""}
                  disabled={disabled}
                  placeholder="comment (optional)"
                  onChange={(e) => handleCommentChange(i, e.target.value)}
                  className="input flex-1 text-body-sm"
                  aria-label={`Comment for ${EVENT_LABELS[event.type]}`}
                  maxLength={2000}
                />
                {/* Delete button */}
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={`Delete event ${EVENT_LABELS[event.type]}`}
                  onClick={() => handleDeleteEvent(i)}
                  className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ol>
      )}

      {/* Add transition chip menu */}
      <div className="space-y-2">
        {chipMenuOpen ? (
          <div
            data-testid="lifecycle-chip-menu"
            className="flex flex-wrap gap-2"
          >
            {CHIP_EVENT_TYPES.map((type) => {
              const invalidTransition = !canTransition(terminalState, type);
              const singleUseViolation =
                SINGLE_USE_TERMINAL.has(type) && presentTypes.has(type);
              const isDisabled = disabled || invalidTransition || singleUseViolation;
              return (
                <button
                  key={type}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleAddChip(type)}
                  className="inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-caption font-medium text-slate-700 transition hover:border-brand-400 hover:text-brand-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
                >
                  {EVENT_LABELS[type]}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setChipMenuOpen(false)}
              className="text-caption text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setChipMenuOpen(true)}
            className="inline-flex items-center gap-1 text-caption font-medium text-brand-600 hover:text-brand-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-not-allowed disabled:opacity-40 dark:text-brand-400 dark:hover:text-brand-300"
          >
            + Add transition
          </button>
        )}
      </div>

      {/* Inline validation error */}
      {!validationResult.ok && (
        <p
          role="alert"
          className="text-caption text-red-600 dark:text-red-400"
        >
          {validationResult.error}
        </p>
      )}
    </fieldset>
  );
}

// Collapsible disclosure for power-user fields (Evidence Links +
// Notes). Closed by default; the open/closed state persists in
// sessionStorage so reopening the next row keeps the preference.
function AdvancedDisclosure({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<boolean>(() => readAdvancedOpen());
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      writeAdvancedOpen(next);
      return next;
    });
  };
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-caption font-medium text-slate-600 hover:text-slate-900 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-300 dark:hover:text-slate-100"
      >
        <ChevronRight
          aria-hidden="true"
          className={`h-3.5 w-3.5 transition-transform ${
            open ? "rotate-90" : ""
          }`}
        />
        Advanced
      </button>
      {open && <div className="space-y-4">{children}</div>}
    </div>
  );
}
